import React, { memo, useCallback, useMemo } from 'react';
/** COMPONENTS */
import WeekDays from '../WeekDays';
import MonthModifiers from '../MonthModifiers';
import InputError from '../../InputError';
import * as RecurrenceUI from './RecurrenceUI';
/** UTILS */
import { Frequency, RRule, Weekday } from 'rrule';
import { SingleSelectMenu } from '../../Dropdown';

interface IProps {
  rrule: any;
  setRule: (rule: any) => void;
  isError: boolean;
  clearError: () => void;
}

const customFrequency: { [id: string]: string } = {
  DAILY: 'Day',
  WEEKLY: 'Week',
  MONTHLY: 'Month',
};

export default memo(function Recurrence({
  rrule,
  setRule,
  isError,
  clearError,
}: IProps) {
  const {
    byhour,
    byminute,
    freq,
    interval,
    byweekday = [],
  } = rrule.origOptions;

  const defaultInterval = useMemo(
    () =>
      Array.from([...Array(31), 60, 90], (value: any, i: number) => {
        if (!value) {
          return {
            key: i + 1,
            value: i + 1,
            text: i + 1,
          };
        }
        return {
          key: value,
          value,
          text: value,
        };
      }),
    [],
  );
  const onIntervalChange = useCallback(
    (value: string) => {
      setRule(
        new RRule({
          ...rrule.origOptions,
          interval: value,
        }),
      );
    },
    [setRule, rrule.origOptions],
  );

  const defaultFrequencies = useMemo(
    () =>
      Object.keys(Frequency)
        .flatMap((freqKey: any) => {
          const currentFrequency = Frequency[freqKey];
          if (
            typeof currentFrequency === 'number' &&
            currentFrequency > 0 &&
            currentFrequency < 4
          ) {
            return {
              text:
                rrule.origOptions.interval > 1
                  ? `${customFrequency[freqKey]}s`
                  : customFrequency[freqKey],
              value: currentFrequency,
              key: currentFrequency,
            };
          }
          return [];
        })
        .reverse(),
    [rrule],
  );
  /**
   * @description on change day/week/month, freq value: month == 1, week == 2, day == 3
   * TODO: @Brett refactor file later. the {value: number} is not intuitive at all
   */
  const onFrequencyChange = useCallback(
    (newValue: string) => {
      const value = !isNaN(Number(newValue)) ? Number(newValue) : null;

      if (value === null) {
        return;
      }

      if (value === 1) {
        // month option
        setRule(
          new RRule({
            ...rrule.origOptions,
            freq: value,
            bymonthday: 1,
          }),
        );
        return;
      }
      setRule(
        new RRule({
          ...rrule.origOptions,
          freq: value,
          byweekday: [],
          bymonthday: [],
        }),
      );
      clearError();
    },
    [setRule, rrule.origOptions, clearError],
  );

  const currentExecutionInterval = useMemo(() => {
    const d = new Date();
    d.setHours(byhour);
    d.setMinutes(byminute);
    return d;
  }, [byhour, byminute]);

  const onExecutionTimeChange = useCallback(
    (date: Date | null) => {
      if (!date) {
        return;
      }

      const currentHour = date.getHours();
      const currentMinute = date.getMinutes();

      setRule(
        new RRule({
          ...rrule.origOptions,
          byhour: currentHour,
          byminute: currentMinute,
        }),
      );
    },
    [setRule, rrule.origOptions],
  );

  const onSelectWeekDay = useCallback(
    (weekday: Weekday) => {
      const isWeekDaySelected = byweekday?.find(
        (currentWeekDay: any) => currentWeekDay === weekday,
      );
      if (isWeekDaySelected) {
        setRule(
          new RRule({
            ...rrule.origOptions,
            byweekday: byweekday.filter(
              (activeWeekDay: any) => activeWeekDay !== weekday,
            ),
          }),
        );
        return;
      }
      setRule(
        new RRule({
          ...rrule.origOptions,
          byweekday: [...byweekday, weekday],
        }),
      );
      clearError();
    },
    [setRule, rrule.origOptions, byweekday, clearError],
  );
  const onSelectMonthModifier = useCallback(
    (newRule: any) => {
      setRule(
        new RRule({
          ...rrule.origOptions,
          ...newRule,
        }),
      );
    },
    [setRule, rrule],
  );

  const canRenderWeekDays = useMemo(() => freq === 2, [freq]);
  const canRenderMonthModifiers = useMemo(() => freq === 1, [freq]);

  return (
    <div>
      <RecurrenceUI.Row>
        <RecurrenceUI.Column>
          <RecurrenceUI.StyledLabel>Every</RecurrenceUI.StyledLabel>
          {canRenderWeekDays ? (
            <RecurrenceUI.StyledLabel>On</RecurrenceUI.StyledLabel>
          ) : canRenderMonthModifiers ? (
            <RecurrenceUI.StyledLabel>On the</RecurrenceUI.StyledLabel>
          ) : null}
          <RecurrenceUI.StyledLabel>Execution Time</RecurrenceUI.StyledLabel>
        </RecurrenceUI.Column>
        <RecurrenceUI.Column>
          <RecurrenceUI.FlexRow>
            <SingleSelectMenu
              options={defaultInterval}
              value={interval}
              onChange={onIntervalChange}
              customWidth="60px"
            />
            <SingleSelectMenu
              options={defaultFrequencies}
              value={freq}
              onChange={onFrequencyChange}
              customWidth="90px"
            />
          </RecurrenceUI.FlexRow>
          <RecurrenceUI.FlexRow>
            {canRenderWeekDays && (
              <WeekDays
                activeWeekDays={byweekday}
                onSelectWeekDay={onSelectWeekDay}
              />
            )}
            {canRenderMonthModifiers && (
              <MonthModifiers
                rrule={rrule}
                onSelectMonthModifier={onSelectMonthModifier}
              />
            )}
            <RecurrenceUI.ExecutionTimeDropdown
              selectedValue={currentExecutionInterval}
              onChange={onExecutionTimeChange}
              positionStrategy="absolute"
            />
          </RecurrenceUI.FlexRow>
        </RecurrenceUI.Column>
      </RecurrenceUI.Row>
      {isError && (
        <RecurrenceUI.FlexRow>
          <InputError
            minified
            error={'You need to select at least one day in a week.'}
          />
        </RecurrenceUI.FlexRow>
      )}
    </div>
  );
});
